import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import {
  MetaGamesEventType,
  type MetagamesCurrentEvent,
  type ClaimPuzzleAnimationState,
} from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import { watchWarmMetagame, WarmPrelaunch } from '../metagames-warm/warmPrelaunch';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class PuzzleStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('puzzle' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      if (isUserAuthorized) {
        this.resolveAndUpdateFlags();
      } else {
        this.setPuzzleFlag(false);
        this.prelaunch.clear();
      }
    });

    watchWarmMetagame({
      rootStore,
      getEvent: () => this.puzzleEvent,
      prelaunch: this.prelaunch,
      preloadGame: 'puzzle',
    });
  }

  private readonly prelaunch = new WarmPrelaunch('PuzzleStore', (coinType, gameId) =>
    this.launchGame(coinType, gameId),
  );

  private async launchGame(coin: CoinType, gameId: number) {
    const response = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', { coin, gameId });

    return response.data?.data;
  }

  private resolveAndUpdateFlags() {
    const puzzleFeatureFlag = resolveFlag('puzzle', true);
    const puzzlePreMessageFlag = resolveFlag('puzzle_pre_message', false);

    this.setPuzzleFlag(puzzleFeatureFlag);
    this.setPuzzlePreMessage(puzzlePreMessageFlag);
  }

  @observable
  public puzzleFlag = false;

  @action
  public setPuzzleFlag(flag: boolean) {
    this.puzzleFlag = flag;
  }

  @observable
  public puzzlePreMessageFlag = false;

  @action
  public setPuzzlePreMessage(flag: boolean) {
    this.puzzlePreMessageFlag = flag;
  }

  @observable
  public puzzleEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setPuzzleEvent(event: MetagamesCurrentEvent | undefined) {
    this.puzzleEvent = event;
  }

  @computed
  public get puzzleEventByCoin() {
    if (!this.puzzleEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.puzzleEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @computed
  public get currentOpenPuzzleEventByCoin() {
    if (!this.currentOpenPuzzleEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.currentOpenPuzzleEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @observable
  public currentOpenPuzzleEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenPuzzleEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenPuzzleEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public claimPuzzleAnimationState: ClaimPuzzleAnimationState = {
    puzzleAmount: 0,
    enable: false,
  };

  @action
  public setClaimPuzzleAnimationState(state: Partial<ClaimPuzzleAnimationState>) {
    this.claimPuzzleAnimationState = { ...this.claimPuzzleAnimationState, ...state };
  }

  public handlePuzzleIframeLoad(iframeRef: HTMLIFrameElement) {
    if (!iframeRef.contentWindow) {
      devError('PuzzleStore handlePuzzleIframeLoad: Iframe content window is not available');
      return;
    }

    this.rootStore.metagamesStore.setIframeRef(iframeRef);

    if (this.currentOpenPuzzleEvent) {
      iframeRef.contentWindow.postMessage(
        {
          type: 'newMetagamesEvent',
          payload: {
            metagamesEvent: toJS(this.currentOpenPuzzleEvent),
          },
        },
        import.meta.env.VITE_GAME_UI_URL,
      );
    }
  }

  private updateEventOptedInStatus(eventId: string, coinType: CoinType, isOptedIn: boolean) {
    if (!this.puzzleEvent) {
      devError('PuzzleStore: updateEventOptedInStatus error: puzzleEvent not found');
      return;
    }

    const coinTypeLower = coinType.toLowerCase() as 'gc' | 'sc';

    if (this.puzzleEvent && this.puzzleEvent.id === eventId && this.puzzleEvent[coinTypeLower]) {
      const updatedCurrentEvent = {
        ...this.puzzleEvent,
        [coinTypeLower]: {
          ...this.puzzleEvent[coinTypeLower],
          isOptedIn,
        },
      };

      this.setPuzzleEvent(updatedCurrentEvent);
    }
  }

  public async acknowledgeOptIn(eventId: string) {
    try {
      const { coinType } = this.rootStore.coinTypeStore;

      this.updateEventOptedInStatus(eventId, coinType, true);

      await this.rootStore.metagamesStore.optInToEvent(eventId, coinType);
    } catch (error) {
      devError('PuzzleStore: acknowledgeOptIn error', error);
    }
  }

  public startDialogs(type?: FeatureManagerDialogType | undefined): Promise<boolean> | boolean {
    switch (type) {
      case 'PuzzlePreMessage': {
        const puzzlePreMessageEvent =
          this.puzzleEventByCoin && !this.puzzleEventByCoin.isOptedIn ? this.puzzleEventByCoin : undefined;

        if (puzzlePreMessageEvent && this.puzzlePreMessageFlag) {
          void this.startPuzzleGame();
          return true;
        }
      }
    }
    return false;
  }

  public async startPuzzleGame(): Promise<string | undefined> {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      let metagamesUrl: string | undefined;

      if (!this.puzzleEvent) {
        devError('PuzzleStore: startPuzzleGame error: puzzleEvent not found');
        return;
      }

      const eventByCoin = this.puzzleEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('PuzzleStore: startPuzzleGame error: event not found');
        return;
      }

      const launchGameData =
        (await this.prelaunch.take(eventByCoin.gameId, currentCoinType)) ??
        (await this.launchGame(currentCoinType, eventByCoin.gameId));

      if (!launchGameData) {
        devError('PuzzleStore: startPuzzleGame error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameData;

      if (metagames) {
        const { token, path, accessToken } = metagames;
        const { gameUiFlag } = this.rootStore.gameStore;

        metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          metagamesToken: gameUiFlag ? token : undefined,
          metagamesAccessToken: gameUiFlag ? accessToken : undefined,
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: currentCoinType,
          flags: JSON.stringify([
            { name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) },
          ]),
        });

        const metagamesEventToSet = {
          ...this.puzzleEvent,
          token,
          path,
          url: metagamesUrl,
        };

        this.setCurrentOpenPuzzleEvent(metagamesEventToSet);

        // launch() returns false when not ready, still warming, or already live — dialog stays the fallback.
        const launchedWarm = this.rootStore.metagamesWarmStore.launch('puzzle', 'launchPuzzleGame', {
          token,
          accessToken,
          metagamesEvent: toJS(this.puzzleEvent),
        });

        if (!launchedWarm) {
          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenMetagamesDialog,
            options: { type: MetaGamesEventType.PUZZLE },
          });
        }
      } else {
        devError('PuzzleStore: startPuzzleGame error: metagames not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('PuzzleStore: startPuzzleGame error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame(dialogType?: FeatureManagerDialogType) {
    // Every exit must reach this: launch() refuses while 'live', so one missed exit makes every later
    // open fall back for the rest of the session.
    if (this.rootStore.metagamesWarmStore.activeKey === 'puzzle') {
      this.rootStore.metagamesWarmStore.close();
    }

    this.setCurrentOpenPuzzleEvent(undefined);
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  public resetEvent() {
    this.setPuzzleEvent(undefined);
    this.setIsLoadingGame(false);
    if (this.currentOpenPuzzleEvent) {
      this.closeGame();
    }
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: 'puzzle',
    });
  }

  public startClaimPuzzleAnimation({
    puzzleAmount,
    onAnimationEnd,
    coinType,
  }: {
    puzzleAmount: number;
    onAnimationEnd?: () => Promise<void> | void;
    coinType?: CoinType;
  }) {
    this.setClaimPuzzleAnimationState({
      puzzleAmount,
      enable: true,
      coinType,
      onAnimationEnd: () => {
        this.setClaimPuzzleAnimationState({ enable: false });
        void onAnimationEnd?.();
      },
    });
  }
}
